import { DateTime } from 'luxon'
import { makeAutoObservable, runInAction } from 'mobx'
import Invoice from 'src/models/Invoice'
import User from 'src/models/User'
import apiClient from 'src/utils/apiClient'
import { GetSampleInvoiceParams } from 'src/utils/apiClient/invoices'

///////////////////////////////////
// Types
///////////////////////////////////

const CACHE_SIZE = 10

type GetInvoiceParams = {
  userId: number
  date: DateTime
}

///////////////////////////////////
// Store
///////////////////////////////////

export default class InvoiceStore {
  private invoiceCaches = new Map<string, Invoice>()
  private billingAmountGuideCaches = new Map<string, number | null>()

  invoice: Invoice | null = null
  invoiceDates: DateTime[] = []

  constructor() {
    makeAutoObservable(this)
  }

  // Setter ------------------------------------------------------

  setInvoice = (invoice: Invoice | null) => {
    this.invoice = invoice
  }

  // Getter ------------------------------------------------------

  getBillingAmountGuide = async ({ userId, date }: GetInvoiceParams) => {
    const cacheKey = this.cacheKey(date)
    const cache = this.billingAmountGuideCaches.get(cacheKey)
    if (cache !== void 0) return cache

    const { billingAmountGuide } = await apiClient.invoices.getBillingAmountGuide({
      userId,
      year: date.year,
      month: date.month,
    })

    this.billingAmountGuideCaches.set(cacheKey, billingAmountGuide)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.billingAmountGuideCaches.size > CACHE_SIZE)
      this.billingAmountGuideCaches.delete(this.billingAmountGuideCaches.keys().next().value)

    return billingAmountGuide
  }

  getInvoice = async ({ userId, date }: GetInvoiceParams) => {
    const cacheKey = this.cacheKey(date)
    const cache = this.invoiceCaches.get(cacheKey)
    if (cache) return this.setInvoice(cache)

    const invoiceData = await apiClient.invoices.getInvoice({
      userId,
      year: date.year,
      month: date.month,
    })

    const invoice = new Invoice(invoiceData)

    this.invoiceCaches.set(cacheKey, invoice)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.invoiceCaches.size > CACHE_SIZE) this.invoiceCaches.delete(this.invoiceCaches.keys().next().value)

    this.setInvoice(invoice)
  }

  getInvoiceDates = async (userId: number) => {
    const dates = await apiClient.invoices.getInvoiceDates({ userId })

    runInAction(() => {
      this.invoiceDates = dates.map((date) => DateTime.fromISO(date))
    })
  }

  getSampleInvoice = async (params: GetSampleInvoiceParams) => {
    const res = await apiClient.invoices.getSampleInvoice(params)
    return {
      invoice: new Invoice(res.invoice),
      user: new User(res.user),
    }
  }

  // Private actions ---------------

  private cacheKey = (date: DateTime) => {
    return date.toFormat('yyyy-MM')
  }
}
